/**
 * Deep links into the console. The SPA has no router — the shell keeps the active tab in state — so a link
 * that lands on a specific tab is read once from the URL at boot. gyro-svc serves index.html for every
 * unknown GET path, which is what makes `/support?request=<id>` reach this code.
 *
 * Producers: the desktop agent's IT-support card ("Open in Gyro Admin") links to
 * `/support?request=<requestId>` (services/gyro-svc/src/supportNotify.ts supportAdminUrl).
 */
export const ADMIN_TABS = [
  'dashboard',
  'adoption',
  'config',
  'caps',
  'voice',
  'apps',
  'access',
  'knowledge',
  'support',
  'skills',
  'pets',
  // Knowledge › Company skills (2026-10-12): the skills an admin publishes for everyone (dark until the service turns them on).
  'company-skills',
  'activity',
  // Audit › Audit log and Audit › Approvals (2026-10-11): the Activity screen's ledger and approvals views as pages of
  // their own. Approvals is the console's one inbox for pending actions (owner decision 2026-10-11 09:40).
  'audit',
  'approvals',
  'analytics',
  'governance',
  // Outfits and mascot › Gallery (2026-10-11): the desktop mascot's outfits, out of Governance onto a page of their own.
  'outfits',
  // Shown only where the service runs the Office add-in (App.tsx probes /v1/admin/office/installs). Not `office`:
  // /office/* is the add-in's own static path on the same origin.
  'office-installs',
  // Operations › PC switches (owner, 2026-10-21): the desktop agent's switches from the server. Shown only where the
  // service has them switched on (App.tsx probes /v1/admin/desktop/policy, which answers 404 while they are off).
  'pc-switches',
  // Pulse (docs/CONTRACT-pulse.md, 2026-10-23): what people ask Gyro, whether it helped, what to fix; nine pages, shown only
  // where the service has the pulse flag on (App.tsx probes /v1/admin/pulse/settings, which answers 404 while it is off).
  'pulse',
  'pulse-asks',
  'pulse-domains',
  'pulse-tools',
  'pulse-feedback',
  'pulse-problems',
  'pulse-digests',
  'pulse-operations',
  'pulse-settings',
  'fleet', // Operations > Fleet health (docs/CONTRACT-fleet-governance.md), shown while the fleet flag is on
  'trends', // Audit > Trends, shown while the governance-trends flag is on
] as const;
export type Tab = (typeof ADMIN_TABS)[number];

const isTab = (v: string): v is Tab => (ADMIN_TABS as readonly string[]).includes(v);

/** `/support` or `/?tab=support` → 'support'; anything unknown → the fallback (the path wins over the query). */
export function tabFromLocation(pathname: string, search: string, fallback: Tab): Tab {
  const seg = pathname.replace(/^\/+|\/+$/g, '').toLowerCase();
  if (isTab(seg)) return seg;
  const q = (new URLSearchParams(search).get('tab') ?? '').trim().toLowerCase();
  return isTab(q) ? q : fallback;
}

/**
 * Where the signed-out gate sends the browser. Away from the root the current location rides along as
 * `returnTo`, so gyro-svc's /auth/callback lands the operator back on the deep link (`/support?request=<id>`)
 * instead of POST_LOGIN_URL. The service honours only a same-origin relative path (services/gyro-svc/src/auth.ts
 * safeReturnTo) and silently falls back otherwise, so this helper never needs to be defensive about the value.
 */
export function loginUrlFor(pathname: string, search: string): string {
  const here = `${pathname || '/'}${search}`;
  return here === '/' ? '/auth/login' : `/auth/login?returnTo=${encodeURIComponent(here)}`;
}

/** Request ids are server-minted opaque tokens; anything outside that shape is not a request and is ignored. */
const REQUEST_ID = /^[A-Za-z0-9._:-]{4,200}$/;

/** The `?request=<id>` a support notification linked to, or null when absent or malformed. */
export function supportRequestFromSearch(search: string): string | null {
  const raw = (new URLSearchParams(search).get('request') ?? '').trim();
  return REQUEST_ID.test(raw) ? raw : null;
}

/**
 * The user guide lives on the server (services/gyro-svc/src/guideRoutes.ts), open to every signed-in employee; the
 * console SPA never owns a /guide path. When the SPA is reached at one anyway (an older build's SPA fallback, a deep
 * link the server did not own), the browser is sent to that same path on the server rather than shown the console's
 * sign-in gate (owner, 2026-10-11: "users shouldnt see gyro admin ... the user guide shoudl be open to all users").
 * Returns the location to leave for, or null when the path is not the guide's.
 */
export function guideLocationFor(pathname: string, search: string): string | null {
  return /^\/guide(?:\/|$)/i.test(pathname) ? `${pathname}${search}` : null;
}
